import { Link } from '@tanstack/react-router'
import { Fragment, useMemo, useState } from 'react'
import { ArrowUpRight, Columns3, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import {
  comparisonColumnGroups,
  getPortfolioColumnCount,
  getComparisonMessage,
  isActualVsBudgetPeriod,
  isPortfolioSort,
  selectVisibleComparisonGroups,
  selectPortfolioCompanies,
  type PortfolioMetric,
  type PortfolioSort,
} from '@/features/actual-vs-budget-production/model'
import type {
  ActualVsBudgetComparison,
  ActualVsBudgetPerformanceClassification,
  ActualVsBudgetPeriod,
  CompanyActualVsBudget,
} from '@/features/actual-vs-budget-production/types'
import { usePortfolioActualVsBudget } from '@/hooks/use-actual-vs-budget'
import { formatCurrency, formatDate, formatPercentage } from '@/lib/format'
import { cn } from '@/lib/utils'

const periodLabels: Record<ActualVsBudgetPeriod, string> = {
  MONTHLY: 'Monthly · latest close',
  QUARTERLY: 'Fiscal quarter to date',
  FYTD: 'Fiscal year to date',
}

const performancePresentation: Record<
  ActualVsBudgetPerformanceClassification,
  { label: string; className: string }
> = {
  ON_PLAN: { label: 'On plan', className: 'text-emerald-700' },
  WATCH: { label: 'Watch', className: 'text-amber-700' },
  NEEDS_ATTENTION: {
    label: 'Needs attention',
    className: 'text-destructive',
  },
  UNAVAILABLE: { label: 'Unavailable', className: 'text-muted-foreground' },
}

function ComparisonCells({
  comparison,
}: {
  comparison: ActualVsBudgetComparison | undefined
}) {
  if (!comparison) {
    return (
      <TableCell
        colSpan={4}
        className="h-20 whitespace-normal bg-muted/20 px-4 text-center"
      >
        <Typography
          variant="body-sm"
          className="whitespace-normal text-muted-foreground"
        >
          Comparison unavailable — Actual data is missing
        </Typography>
      </TableCell>
    )
  }

  const incompleteMessage = getComparisonMessage(comparison)
  if (incompleteMessage) {
    return (
      <TableCell
        colSpan={4}
        className={cn(
          'h-20 whitespace-normal px-4 text-center',
          comparison.state === 'INCOMPLETE_MATCH'
            ? 'bg-amber-50/60'
            : 'bg-muted/20'
        )}
      >
        <Typography
          variant="body-sm"
          className={cn(
            'whitespace-normal break-words',
            comparison.state === 'INCOMPLETE_MATCH'
              ? 'text-amber-900'
              : 'text-muted-foreground'
          )}
        >
          {incompleteMessage}
        </Typography>
      </TableCell>
    )
  }

  const performance =
    performancePresentation[comparison.performanceClassification]
  const hasPerformance = comparison.performanceClassification !== 'UNAVAILABLE'

  return (
    <>
      <TableCell className="h-20 min-w-28 px-3 text-right">
        <Typography
          as="span"
          variant="body-sm"
          className="block font-medium tabular-nums"
        >
          {formatCurrency(comparison.actual)}
        </Typography>
      </TableCell>
      <TableCell className="h-20 min-w-28 px-3 text-right font-medium tabular-nums">
        {formatCurrency(comparison.budget)}
      </TableCell>
      <TableCell
        className={cn(
          'h-20 min-w-28 px-3 text-right font-medium tabular-nums',
          hasPerformance && performance.className
        )}
        aria-label={`Dollar variance ${formatCurrency(comparison.dollarVariance)} — ${performance.label}`}
      >
        {formatCurrency(comparison.dollarVariance)}
      </TableCell>
      <TableCell
        className={cn(
          'h-20 min-w-28 whitespace-normal px-3 text-right font-medium tabular-nums',
          hasPerformance && performance.className
        )}
        aria-label={
          comparison.state === 'ZERO_BUDGET'
            ? 'Percentage unavailable — Budget is $0'
            : `Percentage variance ${formatPercentage(comparison.percentageVariance)} — ${performance.label}`
        }
      >
        {comparison.state === 'ZERO_BUDGET' ? (
          <Typography
            as="span"
            variant="body-sm"
            className="block whitespace-normal text-muted-foreground"
          >
            Percentage unavailable — Budget is $0
          </Typography>
        ) : (
          <>
            <Typography
              as="span"
              variant="body-sm"
              className={cn(
                'block font-medium tabular-nums',
                performance.className
              )}
            >
              {formatPercentage(comparison.percentageVariance)}
            </Typography>
            <Typography
              as="span"
              variant="body-sm"
              className={cn(
                'mt-1 block whitespace-normal text-[10px] font-medium',
                performance.className
              )}
            >
              {performance.label}
            </Typography>
          </>
        )}
      </TableCell>
    </>
  )
}

function CompanyContextCells({ company }: { company: CompanyActualVsBudget }) {
  const fiscalContext = company.fiscalContext
  const coverage = company.budgetCoverage

  return (
    <>
      <TableHead scope="row" className="h-20 min-w-48 bg-background px-3">
        <Button
          asChild
          variant="ghost"
          className="h-auto min-h-11 w-full max-w-full justify-start p-0 font-medium"
        >
          <Link
            to="/companies/$companyId"
            params={{ companyId: company.companyId }}
            aria-label={`Review ${company.companyName} in Company Overview and Financials`}
          >
            <div className="flex min-w-0 flex-col items-start gap-1">
              <Typography
                as="span"
                variant="body-sm"
                className="max-w-full truncate font-medium"
              >
                {company.companyName}
              </Typography>
              <div className="flex items-center gap-1 text-primary/70">
                <Typography as="span" variant="body-sm">
                  Review company
                </Typography>
                <ArrowUpRight
                  className="size-3.5 shrink-0"
                  aria-hidden="true"
                />
              </div>
            </div>
          </Link>
        </Button>
      </TableHead>
      <TableCell className="h-20 min-w-32 px-3">
        <div className="flex flex-wrap gap-1">
          {company.funds.length > 0 ? (
            company.funds.map((fund) => (
              <Badge
                key={fund.id}
                variant="default"
                className="rounded-sm"
                title={fund.displayName}
              >
                {fund.abbreviation}
              </Badge>
            ))
          ) : (
            <Typography
              as="span"
              variant="body-sm"
              className="text-muted-foreground"
            >
              No fund
            </Typography>
          )}
        </div>
      </TableCell>
      <TableCell className="h-20 min-w-48 px-3">
        <Typography as="span" variant="body-sm" className="block font-medium">
          {fiscalContext?.fiscalYearLabel ?? 'Fiscal context unavailable'}
        </Typography>
        <Typography
          as="span"
          variant="body-sm"
          className="mt-1 block whitespace-normal text-muted-foreground"
        >
          Latest close: {formatDate(company.latestClosedActualPeriod)}
        </Typography>
        {fiscalContext && (
          <Typography
            as="span"
            variant="body-sm"
            className="mt-1 block whitespace-normal text-muted-foreground"
          >
            {formatDate(fiscalContext.fiscalYearStart)}–
            {formatDate(fiscalContext.fiscalYearEnd)}
          </Typography>
        )}
      </TableCell>
      <TableCell className="h-20 min-w-48 px-3">
        <Typography as="span" variant="body-sm" className="block font-medium">
          {coverage.available ? 'Budget available' : 'Budget unavailable'}
        </Typography>
        <Typography
          as="span"
          variant="body-sm"
          className="mt-1 block whitespace-normal text-muted-foreground"
        >
          {coverage.available
            ? `${formatDate(coverage.firstPeriod)}–${formatDate(coverage.latestPeriod)}`
            : 'No visible monthly Budget coverage'}
        </Typography>
      </TableCell>
    </>
  )
}

export function PortfolioActualVsBudgetTable() {
  const [period, setPeriod] = useState<ActualVsBudgetPeriod>('FYTD')
  const [query, setQuery] = useState('')
  const [fundId, setFundId] = useState('all')
  const [sort, setSort] = useState<PortfolioSort>('company-asc')
  const [visibleMetrics, setVisibleMetrics] = useState<PortfolioMetric[]>([
    'Revenue',
    'EBITDA',
  ])
  const portfolio = usePortfolioActualVsBudget(period)
  const visibleGroups = useMemo(
    () => selectVisibleComparisonGroups(visibleMetrics),
    [visibleMetrics]
  )
  const columnCount = getPortfolioColumnCount(visibleGroups)

  const funds = useMemo(() => {
    const fundById = new Map<string, { id: string; label: string }>()
    for (const company of portfolio.data?.companies ?? []) {
      for (const fund of company.funds) {
        fundById.set(fund.id, {
          id: fund.id,
          label: fund.abbreviation || fund.displayName,
        })
      }
    }
    return [...fundById.values()].sort((left, right) =>
      left.label.localeCompare(right.label)
    )
  }, [portfolio.data])

  const companies = useMemo(
    () =>
      selectPortfolioCompanies(portfolio.data?.companies ?? [], {
        query,
        fundId,
        sort,
        visibleMetrics,
      }),
    [fundId, portfolio.data, query, sort, visibleMetrics]
  )

  function toggleMetric(metric: PortfolioMetric, checked: boolean) {
    setVisibleMetrics((current) => {
      if (checked) {
        return current.includes(metric) ? current : [...current, metric]
      }
      return current.length === 1
        ? current
        : current.filter((item) => item !== metric)
    })
  }

  if (portfolio.isLoading) {
    return (
      <div
        className="space-y-3"
        role="status"
        aria-label="Loading Actual vs Budget"
      >
        <Skeleton className="h-10 w-full max-w-2xl" />
        <Skeleton className="h-96 w-full rounded-sm" />
      </div>
    )
  }

  if (portfolio.isError) {
    return (
      <div
        role="alert"
        className="rounded-sm border border-primary/10 bg-white p-8 text-center"
      >
        <Typography variant="h5">Actual vs Budget unavailable</Typography>
        <Typography variant="muted" className="mt-2">
          Compass could not load the portfolio comparison.
        </Typography>
        <Button
          type="button"
          variant="outline"
          className="mt-4"
          onClick={() => void portfolio.refetch()}
        >
          Try again
        </Button>
      </div>
    )
  }

  return (
    <section
      aria-labelledby="actual-vs-budget-table-title"
      className="space-y-4"
    >
      <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
        <div>
          <Typography as="h2" variant="h5" id="actual-vs-budget-table-title">
            {period === 'FYTD'
              ? 'FYTD Actual vs Budget'
              : `${periodLabels[period]} Actual vs Budget`}
          </Typography>
          <Typography variant="muted" className="mt-1">
            Compare each company’s Revenue and EBITDA actuals with its budget
            for the selected period. Open a company to continue in Company
            Overview and Financials.
          </Typography>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
          <div className="relative sm:col-span-2 xl:col-span-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              aria-label="Search companies"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search companies"
              className="pl-9"
            />
          </div>
          <Select value={fundId} onValueChange={setFundId}>
            <SelectTrigger aria-label="Filter by fund">
              <SelectValue placeholder="All funds" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All funds</SelectItem>
              {funds.map((fund) => (
                <SelectItem key={fund.id} value={fund.id}>
                  {fund.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={sort}
            onValueChange={(value) => {
              if (isPortfolioSort(value)) setSort(value)
            }}
          >
            <SelectTrigger aria-label="Sort companies">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="company-asc">Company A–Z</SelectItem>
              <SelectItem value="company-desc">Company Z–A</SelectItem>
              <SelectItem value="latest-close">Latest close</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={period}
            onValueChange={(value) => {
              if (isActualVsBudgetPeriod(value)) setPeriod(value)
            }}
          >
            <SelectTrigger aria-label="Comparison period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="FYTD">Fiscal year to date</SelectItem>
              <SelectItem value="QUARTERLY">Fiscal quarter to date</SelectItem>
              <SelectItem value="MONTHLY">Monthly · latest close</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                aria-label="Choose visible metrics"
              >
                <Columns3 className="size-4" aria-hidden="true" />
                Metrics
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Visible metrics</DropdownMenuLabel>
              {comparisonColumnGroups.map(({ metric }) => (
                <DropdownMenuCheckboxItem
                  key={metric}
                  checked={visibleMetrics.includes(metric)}
                  disabled={
                    visibleMetrics.length === 1 &&
                    visibleMetrics.includes(metric)
                  }
                  onCheckedChange={(checked) =>
                    toggleMetric(metric, checked === true)
                  }
                >
                  {metric}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="overflow-x-auto rounded-sm border border-primary/10 bg-white">
        <Table disableWrapper className="min-w-360">
          <TableHeader className="[&_tr>*:first-child]:border-l-0 [&_tr>*:last-child]:border-r-0">
            <TableRow className="bg-primary/[0.04] hover:bg-primary/[0.04]">
              <TableHead rowSpan={2} scope="col" className="w-48 px-3">
                Company
              </TableHead>
              <TableHead rowSpan={2} scope="col" className="w-32 px-3">
                Funds
              </TableHead>
              <TableHead rowSpan={2} scope="col" className="w-48 px-3">
                Fiscal context
              </TableHead>
              <TableHead rowSpan={2} scope="col" className="w-48 px-3">
                Budget coverage
              </TableHead>
              {visibleGroups.map((group) => (
                <TableHead
                  key={group.metric}
                  colSpan={group.colSpan}
                  scope="colgroup"
                  className="border-b-primary/20 bg-primary/[0.07] text-center font-medium"
                >
                  {group.metric}
                </TableHead>
              ))}
            </TableRow>
            <TableRow className="bg-primary/[0.04] hover:bg-primary/[0.04]">
              {visibleGroups.flatMap((group) =>
                group.columns.map((column) => (
                  <TableHead
                    key={`${group.metric}-${column}`}
                    scope="col"
                    className="w-28 px-3 text-right"
                  >
                    {column}
                  </TableHead>
                ))
              )}
            </TableRow>
          </TableHeader>
          <TableBody className="[&_tr>*:first-child]:border-l-0 [&_tr>*:last-child]:border-r-0">
            {companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="h-32 text-center">
                  <Typography variant="muted">
                    No companies match these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow key={company.companyId}>
                  <CompanyContextCells company={company} />
                  {visibleGroups.map((group) => (
                    <Fragment key={group.metric}>
                      <ComparisonCells
                        comparison={company.comparisons.find(
                          ({ requestedMetric }) =>
                            requestedMetric === group.metric
                        )}
                      />
                    </Fragment>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Typography variant="muted">
        Showing {companies.length} of {portfolio.data?.companies.length ?? 0}{' '}
        active companies · {periodLabels[period]}.
      </Typography>
    </section>
  )
}
